import React from 'react';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import optionTypes from '../../../constants/option-types';
import previewGifs from '../constants/preview-gifs';
import computeSpacingFallback from '../helpers/compute-spacing-fallback';

const { Paragraph } = designSystem;
const isMobile = bowser.mobile || bowser.tablet;

const SLIDER_MIN = 0;
const SLIDER_MAX = 128;
const SLIDER_STEP = 1;

const RESTRICTED_COLUMN_GAP_VALUE = 30;
const RESTRICTED_ROW_GAP_VALUE = 48;

const GAPS = {
  horizontal: {
    id: 'horizontal-gap',
    attributes: ['column-gap'],
    iconGlyph: 'gap-vertical',
  },
  vertical: {
    id: 'vertical-gap',
    attributes: ['row-gap'],
    iconGlyph: 'gap-horizontal',
  },
};

const getPopoverProps = ({ imgSrc, title }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {title}
    </Paragraph>
  ),
});

const getValuePopoverProps = (restrictedValue) => ({
  renderContent: () => {
    const desktopMessage = i18next.t(
      'Note: Gap over {value} will be scaled down on tablets and mobile devices.',
      { value: `${restrictedValue}px` }
    );
    const mobileMessage = i18next.t('Note: Gap over {value} will only be shown on desktop.', {
      value: `${restrictedValue}px`,
    });

    return (
      <Paragraph appearance="white" size="small">
        {isMobile ? mobileMessage : desktopMessage}
      </Paragraph>
    );
  },
  checkVisibility: (value) => value > restrictedValue,
});

const getHorizontalGap = () => ({
  ...GAPS.horizontal,
  popover: getPopoverProps({
    imgSrc: previewGifs.COLUMNS_HORIZONTAL_GAP_GIF,
    title: i18next.t('Adjust the horizontal spacing between columns'),
  }),
  valuePopover: getValuePopoverProps(RESTRICTED_COLUMN_GAP_VALUE),
});

const getVerticalGap = () => ({
  ...GAPS.vertical,
  popover: getPopoverProps({
    imgSrc: previewGifs.COLUMNS_VERTICAL_GAP_GIF,
    title: i18next.t('Adjust the vertical spacing between columns'),
  }),
  valuePopover: getValuePopoverProps(RESTRICTED_ROW_GAP_VALUE),
});

const getGaps = (settings = {}) => {
  const gapOptions = [getHorizontalGap(), getVerticalGap()];

  return gapOptions.map((gapOption) => ({
    ...settings,
    ...gapOption,
    type: optionTypes.SLIDER,
    computeFallbackValue: computeSpacingFallback,
    mixedLabel: i18next.t('Mixed'),
    attributes: gapOption.attributes,
    step: SLIDER_STEP,
    min: SLIDER_MIN,
    max: SLIDER_MAX,
    ...(gapOption?.popover && {
      popover: gapOption.popover,
    }),
    ...(gapOption?.valuePopover && {
      valuePopover: gapOption.valuePopover,
    }),
  }));
};

export { GAPS };
export default getGaps;
